import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types d'organisations (nouvelle étape 0)
const ORGANIZATION_TYPES = [
  { 
    id: 'collectivite', 
    label: 'Collectivité', 
    icon: '🏛️', 
    description: 'Mairie, intercommunalité, département, région',
    color: 'green',
    suggestions: {
      tone: 'institutionnel',
      target: 'citoyens',
      examples: {
        description: 'Mairie de 15 000 habitants souhaitant moderniser sa communication avec les citoyens. Nous proposons des services publics locaux : état civil, urbanisme, action sociale, vie associative.',
        targetDesc: 'Citoyens et administrés de la commune, associations locales, acteurs économiques du territoire',
        zone: 'Commune et intercommunalité',
        usp: 'Proximité avec les habitants, connaissance du territoire, service public de qualité',
        challenges: 'Difficulté à toucher les jeunes, communication parfois perçue comme administrative, manque de visibilité sur les réseaux sociaux'
      }
    }
  },
  { 
    id: 'association', 
    label: 'Association', 
    icon: '🤝', 
    description: 'ONG, structure ESS, association locale',
    color: 'emerald',
    suggestions: {
      tone: 'engagé',
      target: 'membres',
      examples: {
        description: 'Association d\'aide aux personnes en situation de précarité. Nous proposons des distributions alimentaires, un accompagnement social et des ateliers de réinsertion professionnelle.',
        targetDesc: 'Bénéficiaires, donateurs, bénévoles, partenaires institutionnels et entreprises mécènes',
        zone: 'Département et région',
        usp: 'Engagement de terrain depuis 10 ans, équipe de bénévoles mobilisés, partenariats solides avec les acteurs locaux',
        challenges: 'Besoin de recruter plus de bénévoles, développer les dons, communiquer sur notre impact'
      }
    }
  },
  { 
    id: 'pme', 
    label: 'PME', 
    icon: '🏢', 
    description: 'Artisan, commerçant, prestataire de services',
    color: 'teal',
    suggestions: {
      tone: 'commercial',
      target: 'clients',
      examples: {
        description: 'Entreprise de rénovation énergétique spécialisée dans l\'isolation et les pompes à chaleur. Nous accompagnons les particuliers et professionnels dans leurs projets de rénovation.',
        targetDesc: 'Propriétaires de maisons individuelles souhaitant réduire leur facture énergétique, copropriétés, entreprises locales',
        zone: 'Rayon de 50 km autour de notre agence',
        usp: 'Certifications RGE, devis gratuit sous 48h, garantie décennale, accompagnement pour les aides financières',
        challenges: 'Concurrence forte, besoin de générer plus de demandes de devis, se démarquer des artisans non certifiés'
      }
    }
  },
  { 
    id: 'porteur', 
    label: 'Porteur de projet', 
    icon: '🚀', 
    description: 'Startup, entrepreneur, initiative citoyenne',
    color: 'cyan',
    suggestions: {
      tone: 'dynamique',
      target: 'early-adopters',
      examples: {
        description: 'Startup développant une application de covoiturage local pour les zones rurales. Notre solution connecte les habitants des petites communes pour faciliter leurs déplacements quotidiens.',
        targetDesc: 'Habitants des zones rurales sans transport en commun, collectivités souhaitant améliorer la mobilité, entreprises locales',
        zone: 'Territoire pilote puis déploiement national',
        usp: 'Solution pensée pour les territoires ruraux, partenariats avec des collectivités, modèle économique solidaire',
        challenges: 'Faire connaître notre solution, convaincre les premières collectivités partenaires, atteindre la masse critique d\'utilisateurs'
      }
    }
  },
];

// Business Types avec icônes et labels français
const BUSINESS_TYPES = [
  { id: 'artisan', label: 'Artisan', icon: '🔨', description: 'Services ou produits faits main' },
  { id: 'coach', label: 'Coach / Formateur', icon: '🎯', description: 'Accompagnement et transformation' },
  { id: 'consultant', label: 'Consultant', icon: '💼', description: 'Expertise et conseil B2B' },
  { id: 'ecommerce', label: 'E-commerce', icon: '🛒', description: 'Vente en ligne de produits' },
  { id: 'immobilier', label: 'Immobilier', icon: '🏠', description: 'Agent, promoteur, investisseur' },
  { id: 'restaurant', label: 'Restaurant', icon: '🍽️', description: 'Restauration et food' },
  { id: 'saas', label: 'SaaS / Tech', icon: '💻', description: 'Logiciel et technologie' },
  { id: 'freelance', label: 'Freelance', icon: '✨', description: 'Services indépendants' },
  { id: 'agence', label: 'Agence', icon: '🏢', description: 'Services professionnels' },
  { id: 'formation', label: 'Formation', icon: '📚', description: 'Organisme de formation' },
  { id: 'sante', label: 'Santé / Bien-être', icon: '❤️', description: 'Professionnels de santé' },
  { id: 'beaute', label: 'Beauté', icon: '💅', description: 'Esthétique et soins' },
  { id: 'sport', label: 'Sport / Fitness', icon: '💪', description: 'Coaching sportif' },
  { id: 'service_public', label: 'Service public', icon: '🏛️', description: 'Administration et collectivités' },
  { id: 'association', label: 'Association', icon: '🤝', description: 'Structure associative' },
  { id: 'autre', label: 'Autre', icon: '🌟', description: 'Autre activité' },
];

const BUSINESS_GOALS = [
  { id: 'sales', label: 'Augmenter mes ventes', icon: '💰', description: 'Générer plus de chiffre d\'affaires' },
  { id: 'leads', label: 'Générer des prospects', icon: '🎯', description: 'Attirer des clients potentiels' },
  { id: 'awareness', label: 'Gagner en visibilité', icon: '📢', description: 'Développer ma notoriété' },
  { id: 'retention', label: 'Fidéliser mes clients', icon: '🤝', description: 'Garder mes clients actuels' },
  { id: 'engagement', label: 'Mobiliser ma communauté', icon: '✊', description: 'Engager membres et bénévoles' },
  { id: 'impact', label: 'Augmenter mon impact', icon: '🌍', description: 'Renforcer mon action sur le terrain' },
];

const StrategyWizard = ({ onComplete, onBack, isDemo = true }) => {
  const [currentStep, setCurrentStep] = useState(0); // Commence à 0 maintenant
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [showSuggestions, setShowSuggestions] = useState({});
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 0 - Type d'organisation
    organization_type: '',
    // Step 1
    business_description: '',
    business_type: '',
    business_name: '',
    // Step 2
    target_audience: 'b2c',
    target_description: '',
    price_range: '',
    geographic_zone: '',
    // Step 3
    primary_goal: 'sales',
    secondary_goals: [],
    unique_selling_point: '',
    current_challenges: '',
  });

  // Obtenir les suggestions pour le type d'organisation sélectionné
  const getSelectedOrgSuggestions = () => {
    const org = ORGANIZATION_TYPES.find(o => o.id === formData.organization_type);
    return org?.suggestions?.examples || null;
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Appliquer une suggestion
  const applySuggestion = (field, value) => {
    updateForm(field, value);
    setShowSuggestions(prev => ({ ...prev, [field]: false }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.organization_type !== '';
      case 1:
        return formData.business_description.length >= 20 && formData.business_type;
      case 2:
        return formData.target_audience;
      case 3:
        return formData.primary_goal;
      default:
        return false;
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError('');
    setProgress(0);
    setGenerationStep('Initialisation...');

    try {
      // Simuler la progression pendant la requête
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + Math.random() * 15, 90));
      }, 800);

      const steps = [
        { text: 'Analyse de votre marché...', delay: 1000 },
        { text: 'Identification de votre client idéal...', delay: 2000 },
        { text: 'Création de votre positionnement...', delay: 3000 },
        { text: 'Construction de vos arguments de vente...', delay: 4000 },
        { text: 'Finalisation de votre stratégie...', delay: 5000 },
      ];

      let stepIndex = 0;
      const stepInterval = setInterval(() => {
        if (stepIndex < steps.length) {
          setGenerationStep(steps[stepIndex].text);
          stepIndex++;
        }
      }, 1500);

      // Appel API
      const response = await fetch(`${API_URL}/api/projects/demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.business_name || `Stratégie ${formData.business_type}`,
          wizard_input: {
            step0: {
              organization_type: formData.organization_type,
            },
            step1: {
              business_description: formData.business_description,
              business_type: formData.business_type,
              business_name: formData.business_name || null,
            },
            step2: {
              target_audience: formData.target_audience,
              target_description: formData.target_description || null,
              price_range: formData.price_range || null,
              geographic_zone: formData.geographic_zone || null,
            },
            step3: {
              primary_goal: formData.primary_goal,
              secondary_goals: formData.secondary_goals,
              unique_selling_point: formData.unique_selling_point || null,
              current_challenges: formData.current_challenges || null,
            },
          },
          is_demo: isDemo,
        }),
      });

      clearInterval(progressInterval);
      clearInterval(stepInterval);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de la génération');
      }

      const result = await response.json();
      setProgress(100);
      setGenerationStep('Stratégie générée !');
      
      setTimeout(() => {
        onComplete(result);
      }, 500);

    } catch (err) {
      setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
      setIsGenerating(false);
    }
  };

  // Render Steps
  
  // Nouvelle étape 0 : Choix du type d'organisation
  const renderStep0 = () => (
    <div className="space-y-6" data-testid="wizard-step-0">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 mb-4">
          <span className="text-3xl">🎯</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Quel type d'organisation êtes-vous ?</h2>
        <p className="text-gray-400">Nous adapterons les suggestions à votre contexte pour une stratégie sur-mesure.</p>
      </div>

      {/* Organization Type Selection */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {ORGANIZATION_TYPES.map((org) => (
          <button
            key={org.id}
            onClick={() => {
              updateForm('organization_type', org.id);
              // Pré-sélectionner un business_type adapté
              if (org.id === 'collectivite') updateForm('business_type', 'service_public');
              else if (org.id === 'association') updateForm('business_type', 'association');
              else if (org.id === 'pme') updateForm('business_type', '');
              else if (org.id === 'porteur') updateForm('business_type', 'saas');
            }}
            className={`p-6 rounded-2xl border text-left transition-all group ${
              formData.organization_type === org.id
                ? `bg-${org.color}-500/20 border-${org.color}-500/50 text-white`
                : 'bg-gray-900/30 border-gray-700 text-gray-400 hover:border-gray-600 hover:bg-gray-800/50'
            }`}
            data-testid={`wizard-org-${org.id}`}
          >
            <div className="flex items-start space-x-4">
              <span className="text-4xl group-hover:scale-110 transition-transform">{org.icon}</span>
              <div>
                <span className="text-lg font-semibold block text-white">{org.label}</span>
                <span className="text-sm text-gray-400 block mt-1">{org.description}</span>
              </div>
            </div>
            {formData.organization_type === org.id && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <span className="text-xs text-green-400 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  Des suggestions adaptées vous seront proposées
                </span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Info box */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 mt-6">
        <div className="flex items-start space-x-3">
          <span className="text-xl">💡</span>
          <div>
            <p className="text-sm text-gray-300 font-medium">Pourquoi cette question ?</p>
            <p className="text-xs text-gray-500 mt-1">
              Chaque type d'organisation a ses spécificités. En connaissant votre contexte, nous vous proposerons 
              des exemples et suggestions pertinents pour accélérer la création de votre stratégie.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => {
    const suggestions = getSelectedOrgSuggestions();
    
    return (
    <div className="space-y-6" data-testid="wizard-step-1">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 mb-4">
          <span className="text-3xl">💡</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Décrivez votre activité</h2>
        <p className="text-gray-400">En quelques phrases, expliquez ce que vous faites et ce que vous proposez.</p>
      </div>

      {/* Business Description with Suggestion */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Votre activité <span className="text-red-400">*</span>
        </label>
        <textarea
          value={formData.business_description}
          onChange={(e) => updateForm('business_description', e.target.value)}
          placeholder={suggestions?.description || "Ex: Je suis coach en développement personnel. J'accompagne les entrepreneurs qui veulent retrouver confiance en eux et atteindre leurs objectifs professionnels."}
          className="w-full h-36 bg-gray-900/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all resize-none"
          data-testid="wizard-description"
        />
        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-gray-500">
            {formData.business_description.length < 20 
              ? '💡 Plus de détails = meilleure stratégie' 
              : formData.business_description.length < 80
              ? '👍 Bien ! Vous pouvez ajouter plus de détails'
              : formData.business_description.length < 150
              ? '✨ Très bien ! Description détaillée'
              : '🌟 Excellent ! Description très complète'}
          </span>
          <span className={`text-xs ${formData.business_description.length >= 20 ? 'text-green-400' : 'text-gray-500'}`}>
            {formData.business_description.length} caractères
          </span>
        </div>
        
        {/* Suggestion button */}
        {suggestions?.description && formData.business_description.length < 20 && (
          <button
            onClick={() => applySuggestion('business_description', suggestions.description)}
            className="mt-3 text-sm text-green-400 hover:text-green-300 flex items-center transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            Utiliser un exemple adapté à mon profil
          </button>
        )}
      </div>

      {/* Business Type */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Type d'activité <span className="text-red-400">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {BUSINESS_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => updateForm('business_type', type.id)}
              className={`p-3 rounded-xl border text-left transition-all ${
                formData.business_type === type.id
                  ? 'bg-green-500/20 border-green-500/50 text-white'
                  : 'bg-gray-900/30 border-gray-700 text-gray-400 hover:border-gray-600 hover:text-white'
              }`}
              data-testid={`wizard-type-${type.id}`}
            >
              <span className="text-xl block mb-1">{type.icon}</span>
              <span className="text-sm font-medium block">{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Business Name (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Nom de votre organisation <span className="text-gray-500 text-xs">(optionnel)</span>
        </label>
        <input
          type="text"
          value={formData.business_name}
          onChange={(e) => updateForm('business_name', e.target.value)}
          placeholder="Ex: Mairie de Trévoux, Association Solidarité Loire, Dupont Rénovation..."
          className="w-full bg-gray-900/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
          data-testid="wizard-business-name"
        />
      </div>
    </div>
    );
  };

  const renderStep2 = () => {
    const suggestions = getSelectedOrgSuggestions();
    
    return (
    <div className="space-y-6" data-testid="wizard-step-2">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 mb-4">
          <span className="text-3xl">🎯</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Qui est votre public cible ?</h2>
        <p className="text-gray-400">Décrivez le profil de votre cible pour une stratégie personnalisée.</p>
      </div>

      {/* Target Audience */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Vous vous adressez à <span className="text-red-400">*</span>
        </label>
        <div className="grid grid-cols-3 gap-4">
          {[
            { id: 'b2c', label: 'Particuliers', icon: '👤', desc: 'Citoyens, membres, clients' },
            { id: 'b2b', label: 'Organisations', icon: '🏢', desc: 'Entreprises, institutions' },
            { id: 'both', label: 'Les deux', icon: '🤝', desc: 'Public mixte' },
          ].map((option) => (
            <button
              key={option.id}
              onClick={() => updateForm('target_audience', option.id)}
              className={`p-4 rounded-xl border text-center transition-all ${
                formData.target_audience === option.id
                  ? 'bg-purple-500/20 border-purple-500/50 text-white'
                  : 'bg-gray-900/30 border-gray-700 text-gray-400 hover:border-gray-600'
              }`}
              data-testid={`wizard-audience-${option.id}`}
            >
              <span className="text-2xl block mb-2">{option.icon}</span>
              <span className="font-medium block">{option.label}</span>
              <span className="text-xs text-gray-500 block mt-1">{option.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Target Description with Suggestion */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Décrivez votre public cible <span className="text-gray-500 text-xs">(optionnel mais recommandé)</span>
        </label>
        <textarea
          value={formData.target_description}
          onChange={(e) => updateForm('target_description', e.target.value)}
          placeholder={suggestions?.targetDesc || "Ex: Entrepreneurs de 35-50 ans, dirigeants de PME, qui se sentent débordés..."}
          className="w-full h-24 bg-gray-900/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all resize-none"
          data-testid="wizard-target-desc"
        />
        {suggestions?.targetDesc && !formData.target_description && (
          <button
            onClick={() => applySuggestion('target_description', suggestions.targetDesc)}
            className="mt-2 text-sm text-purple-400 hover:text-purple-300 flex items-center transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            Utiliser la suggestion
          </button>
        )}
      </div>

      {/* Geographic Zone with Suggestion */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Zone géographique <span className="text-gray-500 text-xs">(optionnel)</span>
        </label>
        <input
          type="text"
          value={formData.geographic_zone}
          onChange={(e) => updateForm('geographic_zone', e.target.value)}
          placeholder={suggestions?.zone || "Ex: France entière, Région parisienne, International..."}
          className="w-full bg-gray-900/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
          data-testid="wizard-zone"
        />
        {suggestions?.zone && !formData.geographic_zone && (
          <button
            onClick={() => applySuggestion('geographic_zone', suggestions.zone)}
            className="mt-2 text-sm text-purple-400 hover:text-purple-300 flex items-center transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            Utiliser la suggestion
          </button>
        )}
      </div>
    </div>
    );
  };

  const renderStep3 = () => {
    const suggestions = getSelectedOrgSuggestions();
    const orgType = formData.organization_type;
    
    // Filtrer les objectifs selon le type d'organisation
    const relevantGoals = BUSINESS_GOALS.filter(goal => {
      if (orgType === 'collectivite' || orgType === 'association') {
        return ['awareness', 'engagement', 'impact', 'leads'].includes(goal.id);
      }
      return ['sales', 'leads', 'awareness', 'retention'].includes(goal.id);
    });
    
    return (
    <div className="space-y-6" data-testid="wizard-step-3">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 mb-4">
          <span className="text-3xl">🚀</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Quel est votre objectif ?</h2>
        <p className="text-gray-400">Définissez votre priorité pour une stratégie marketing adaptée.</p>
      </div>

      {/* Primary Goal */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Objectif principal <span className="text-red-400">*</span>
        </label>
        <div className="grid grid-cols-2 gap-4">
          {relevantGoals.map((goal) => (
            <button
              key={goal.id}
              onClick={() => updateForm('primary_goal', goal.id)}
              className={`p-4 rounded-xl border text-left transition-all ${
                formData.primary_goal === goal.id
                  ? 'bg-orange-500/20 border-orange-500/50 text-white'
                  : 'bg-gray-900/30 border-gray-700 text-gray-400 hover:border-gray-600'
              }`}
              data-testid={`wizard-goal-${goal.id}`}
            >
              <span className="text-2xl block mb-2">{goal.icon}</span>
              <span className="font-medium block">{goal.label}</span>
              <span className="text-xs text-gray-500 block mt-1">{goal.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Unique Selling Point with Suggestion */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Ce qui vous différencie <span className="text-gray-500 text-xs">(optionnel mais puissant)</span>
        </label>
        <textarea
          value={formData.unique_selling_point}
          onChange={(e) => updateForm('unique_selling_point', e.target.value)}
          placeholder={suggestions?.usp || "Ex: 15 ans d'expérience, méthode unique, garantie résultats, accompagnement personnalisé..."}
          className="w-full h-20 bg-gray-900/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all resize-none"
          data-testid="wizard-usp"
        />
        {suggestions?.usp && !formData.unique_selling_point && (
          <button
            onClick={() => applySuggestion('unique_selling_point', suggestions.usp)}
            className="mt-2 text-sm text-orange-400 hover:text-orange-300 flex items-center transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            Utiliser la suggestion
          </button>
        )}
      </div>

      {/* Current Challenges with Suggestion */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Vos défis actuels <span className="text-gray-500 text-xs">(optionnel)</span>
        </label>
        <textarea
          value={formData.current_challenges}
          onChange={(e) => updateForm('current_challenges', e.target.value)}
          placeholder={suggestions?.challenges || "Ex: Je n'arrive pas à me démarquer, mes publicités ne convertissent pas, je manque de temps..."}
          className="w-full h-20 bg-gray-900/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all resize-none"
          data-testid="wizard-challenges"
        />
        {suggestions?.challenges && !formData.current_challenges && (
          <button
            onClick={() => applySuggestion('current_challenges', suggestions.challenges)}
            className="mt-2 text-sm text-orange-400 hover:text-orange-300 flex items-center transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            Utiliser la suggestion
          </button>
        )}
      </div>
    </div>
    );
  };

  const renderGenerating = () => (
    <div className="text-center py-12" data-testid="wizard-generating">
      <div className="relative mb-8">
        {/* Animated background */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-32 h-32 bg-green-500/20 rounded-full animate-ping"></div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-24 h-24 bg-green-500/30 rounded-full animate-pulse"></div>
        </div>
        <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg shadow-green-500/30">
          <svg className="w-10 h-10 text-white animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-white mb-2">Génération de votre stratégie</h2>
      <p className="text-gray-400 mb-8">{generationStep}</p>

      {/* Progress bar */}
      <div className="max-w-md mx-auto">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-500">Progression</span>
          <span className="text-green-400 font-medium">{Math.round(progress)}%</span>
        </div>
        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* What's being generated */}
      <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg mx-auto">
        {[
          { icon: '👤', label: 'Avatar Client', done: progress > 30 },
          { icon: '🎯', label: 'Positionnement', done: progress > 50 },
          { icon: '💡', label: 'Arguments', done: progress > 70 },
        ].map((item, idx) => (
          <div 
            key={idx}
            className={`p-3 rounded-xl border transition-all ${
              item.done 
                ? 'bg-green-500/20 border-green-500/30 text-green-400' 
                : 'bg-gray-900/30 border-gray-800 text-gray-600'
            }`}
          >
            <span className="text-xl block mb-1">{item.icon}</span>
            <span className="text-xs font-medium">{item.label}</span>
            {item.done && (
              <svg className="w-4 h-4 mx-auto mt-1 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (isGenerating) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
        <div className="max-w-xl w-full">
          {renderGenerating()}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="strategy-wizard">
      {/* Header */}
      <header className="border-b border-gray-800 bg-[#0a0a0f]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
            data-testid="wizard-back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            <span>Retour</span>
          </button>

          {/* Progress indicator - 4 steps now */}
          <div className="flex items-center space-x-2">
            {[0, 1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    step < currentStep
                      ? 'bg-green-500 text-white'
                      : step === currentStep
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/30'
                        : 'bg-gray-800 text-gray-500'
                  }`}
                >
                  {step < currentStep ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  ) : step + 1}
                </div>
                {step < 3 && (
                  <div className={`w-8 h-0.5 ${step < currentStep ? 'bg-green-500' : 'bg-gray-800'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="w-20"></div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
            {error}
          </div>
        )}

        {currentStep === 0 && renderStep0()}
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-800">
          <button
            onClick={() => setCurrentStep(prev => prev - 1)}
            disabled={currentStep === 0}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              currentStep === 0
                ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
            data-testid="wizard-prev"
          >
            Précédent
          </button>

          {currentStep < 3 ? (
            <button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceed()}
              className={`px-8 py-3 rounded-xl font-medium transition-all ${
                canProceed()
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-lg shadow-green-500/30'
                  : 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
              }`}
              data-testid="wizard-next"
            >
              Continuer
            </button>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={!canProceed()}
              className={`px-8 py-3 rounded-xl font-semibold transition-all flex items-center space-x-2 ${
                canProceed()
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-lg shadow-green-500/30'
                  : 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
              }`}
              data-testid="wizard-generate"
            >
              <span>Générer ma stratégie</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default StrategyWizard;
